import { useMemo, useState } from 'react';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { formatDate } from '../lib/format';
import { useResource } from '../lib/hooks';

/**
 * The audit trail.
 *
 * ITS OWN PAGE, UNDER SETTINGS, rather than a tab in Admin. It started as an
 * Admin tab and that was the wrong home: Admin is the back office — contracts,
 * procurement, the depot, the SOP register — and this is not one of the things
 * the admin function OWNS. It belongs with what the app is configured with and
 * held to, beside the directory and the process library.
 *
 * The Settings nav section is already adminOnly, which matches requireAdmin on
 * the API mount exactly, so this page inherits the gate rather than carrying its
 * own copy of the rule.
 *
 * WHY IT LOOKS LIKE A LOG AND NOT A DASHBOARD. The question this answers is
 * "who changed this, and when" — asked by somebody with a specific thing in
 * mind, usually an examiner or whoever is working out how a record got into its
 * current state. That wants a filterable list in reverse order, not charts.
 *
 * ADMIN ONLY, and the tab is hidden for everybody else. The API is the real gate
 * (requireAdmin on the mount in server.ts); hiding the tab only avoids showing
 * somebody a page that would answer 403.
 *
 * There is no edit and no delete, here or in the API. An audit log the
 * application can amend is not evidence.
 */

interface AuditRow {
  AuditId: number;
  OccurredUtc: string;
  EntityType: string;
  EntityId: string | null;
  Action: 'create' | 'update' | 'delete';
  Changes: string | null;
  UserId: number | null;
  UserName: string | null;
  UserEmail: string | null;
  AppKey: string | null;
  AppName: string | null;
}

interface AuditFacets {
  entityTypes: { EntityType: string; N: number }[];
  users: { UserId: number; DisplayName: string | null; Email: string | null; N: number }[];
  summary: {
    Total: number;
    Creates: number;
    Updates: number;
    Deletes: number;
    LastSevenDays: number;
    Earliest: string | null;
    Latest: string | null;
  } | null;
}

/** What the middleware records, as far as this page needs to read it. */
interface ChangeShape {
  method?: string;
  path?: string;
  status?: number;
  ms?: number;
  body?: unknown;
  reason?: string;
}

function parseChanges(raw: string | null): ChangeShape | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ChangeShape;
  } catch {
    return null;
  }
}

const ACTION_LABEL: Record<AuditRow['Action'], string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

export function AuditTrailPage() {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const params = useMemo(() => {
    const p = new URLSearchParams({ limit: '200' });
    if (entityType) p.set('entityType', entityType);
    if (action) p.set('action', action);
    if (userId) p.set('userId', userId);
    if (q) p.set('q', q);
    return p.toString();
  }, [entityType, action, userId, q]);

  const rows = useResource<AuditRow[]>(`/audit?${params}`);
  const facets = useResource<AuditFacets>('/audit/facets');
  const summary = facets.data?.summary;

  return (
    <>
      <PageHead
        title="Audit trail"
        lede="Every create, update and delete made through this app — who, what, when, and whether it was allowed. Read-only by design: nothing in the app can amend an entry, which is what makes it evidence."
      />

      {rows.error && <ErrorBanner message={rows.error} />}

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Summary</span>
            <h2>What the trail holds</h2>
            <p className="sub">
              Refused attempts are recorded alongside the ones that succeeded — a log of only the
              permitted actions cannot answer "who tried".
            </p>
          </div>
        </div>
        {summary && (
          <div className="card-body grid grid-4">
            <Stat label="Entries" value={summary.Total} />
            <Stat label="Last 7 days" value={summary.LastSevenDays} />
            <Stat
              label="Created / updated / deleted"
              value={`${summary.Creates} / ${summary.Updates} / ${summary.Deletes}`}
            />
            <Stat
              label="Earliest entry"
              value={summary.Earliest ? formatDate(summary.Earliest) : '—'}
            />
          </div>
        )}
      </section>

      <section className="card mb-4">
        <div className="card-body">
          <form
            className="grid grid-4"
            onSubmit={(e) => {
              e.preventDefault();
              setQ(term);
            }}
          >
            <Field label="Area">
              <select
                className="input"
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
              >
                <option value="">Everything</option>
                {facets.data?.entityTypes.map((t) => (
                  <option key={t.EntityType} value={t.EntityType}>
                    {t.EntityType} ({t.N})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Action">
              <select className="input" value={action} onChange={(e) => setAction(e.target.value)}>
                <option value="">Any</option>
                <option value="create">Created</option>
                <option value="update">Updated</option>
                <option value="delete">Deleted</option>
              </select>
            </Field>
            <Field label="Who">
              <select className="input" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Anyone</option>
                {facets.data?.users.map((u) => (
                  <option key={u.UserId} value={String(u.UserId)}>
                    {u.DisplayName ?? u.Email ?? `User ${u.UserId}`} ({u.N})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Search" hint="Matches the path and the recorded payload.">
              <div className="row">
                <input
                  className="input grow"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="e.g. sherweb"
                />
                <button type="submit" className="btn btn-ghost">
                  Find
                </button>
              </div>
            </Field>
          </form>
        </div>
      </section>

      {rows.loading ? (
        <Loading label="Reading the audit trail" />
      ) : (
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <h3>
                {rows.data?.length ?? 0} entr{(rows.data?.length ?? 0) === 1 ? 'y' : 'ies'}
                {(rows.data?.length ?? 0) === 200 ? ' (most recent 200)' : ''}
              </h3>
            </div>
          </div>
          <div className="card-body stack" style={{ gap: 'var(--space-2)' }}>
            {(rows.data?.length ?? 0) === 0 && (
              <p className="small muted">
                Nothing matches. The trail starts at the first change made after it was switched
                on — it cannot show what happened before that.
              </p>
            )}
            {rows.data?.map((r) => {
              const ch = parseChanges(r.Changes);
              /* A 4xx or 5xx is the interesting case, so it is called out rather
                 than left for the reader to spot in a JSON blob. */
              const refused = ch?.status !== undefined && ch.status >= 400;
              return (
                <div key={r.AuditId} className="mini-row" style={{ alignItems: 'flex-start' }}>
                  <span className="xs muted mono" style={{ width: 132, flex: 'none' }}>
                    {formatDate(r.OccurredUtc)}
                  </span>
                  <span className="xs mono" style={{ width: 62, flex: 'none' }}>
                    {ACTION_LABEL[r.Action]}
                  </span>
                  <div className="grow">
                    <div className="small strong">
                      {r.EntityType}
                      {r.EntityId ? <span className="muted"> · {r.EntityId}</span> : null}
                      {refused && (
                        <span className="xs mono ml-2" style={{ color: 'var(--accent-500)' }}>
                          refused {ch?.status}
                        </span>
                      )}
                    </div>
                    <div className="xs muted mono">
                      {ch?.method} {ch?.path}
                      {ch?.ms !== undefined ? ` · ${ch.ms}ms` : ''}
                    </div>
                    {expanded === r.AuditId && (
                      <pre
                        className="xs mono"
                        style={{
                          marginTop: 'var(--space-2)',
                          padding: 'var(--space-3)',
                          background: 'var(--surface-sunken)',
                          borderRadius: 'var(--radius-sm)',
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {r.Changes ?? '(nothing recorded)'}
                      </pre>
                    )}
                  </div>
                  <span className="xs muted" style={{ width: 140, flex: 'none' }}>
                    {r.UserName ?? r.UserEmail ?? <em>not signed in</em>}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setExpanded(expanded === r.AuditId ? null : r.AuditId)}
                  >
                    {expanded === r.AuditId ? 'Hide' : 'Detail'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="strong" style={{ fontSize: 'var(--text-xl)' }}>
        {value}
      </div>
    </div>
  );
}
